import React, { useEffect, useMemo, useState } from "react";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChevronLeft, Heart, Share2, X } from "lucide-react";
import CustomImage from "../common/Image";
import { cn } from "@/lib/utils";
import { copyToClipboard, shareContent } from "@/lib/capacitor";
import { routes } from "@/lib/constants";
import { getWebBaseUrlFromEnv } from "@/lib/env";
import { toast } from "sonner";
import { IProduct, IProductVariant } from "muminsmart-utils";
import { useNavigate } from "react-router-dom";
import { ProductReviewProps } from "../cart/ProductReview";

export interface ProductImageCarouselProps {
  isPreview?: boolean;
  onSeePreview?: () => void;
  isWishlistLoading: boolean;
  handleWishlistToggle: () => Promise<void>;
  isWishlisted: boolean;
  availableStock: number;
  selectedVariant: IProductVariant | null;
  product: IProduct;
  reviewSummary?: ProductReviewProps["reviewSummary"];
}

const ProductImageCarousel = ({
  product,
  selectedVariant,
  isWishlisted,
  isWishlistLoading,
  handleWishlistToggle,
  isPreview,
  onSeePreview,
  availableStock,
}: ProductImageCarouselProps) => {
  const navigate = useNavigate();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [previewStartIndex, setPreviewStartIndex] = useState(0);
  const [previewApi, setPreviewApi] = useState<CarouselApi>();
  const [previewCurrent, setPreviewCurrent] = useState(0);
  const selectedVariantImageKeys = useMemo(() => {
    if (!selectedVariant) {
      return [];
    }

    return [
      selectedVariant.featuredImage,
      ...selectedVariant.additionalImages,
    ].filter(Boolean);
  }, [selectedVariant]);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  useEffect(() => {
    if (!api) {
      return;
    }

    api.scrollTo(0);
    setCurrent(1);
    setCount(api.scrollSnapList().length);
  }, [api, selectedVariant]);

  useEffect(() => {
    if (!previewApi) {
      return;
    }

    setPreviewCurrent(previewApi.selectedScrollSnap() + 1);

    previewApi.on("select", () => {
      setPreviewCurrent(previewApi.selectedScrollSnap() + 1);
    });
  }, [previewApi]);

  useEffect(() => {
    if (!previewApi || !isImagePreviewOpen) {
      return;
    }

    previewApi.scrollTo(previewStartIndex, true);
    setPreviewCurrent(previewStartIndex + 1);
  }, [isImagePreviewOpen, previewApi, previewStartIndex]);

  useEffect(() => {
    setIsImagePreviewOpen(false);
    setPreviewStartIndex(0);
  }, [selectedVariant]);

  useEffect(() => {
    if (!isImagePreviewOpen) {
      return;
    }

    const stateId = { imagePreviewOpen: true };
    window.history.pushState(stateId, "");

    const handlePopState = () => {
      setIsImagePreviewOpen(false);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      if (window.history.state?.imagePreviewOpen) {
        window.history.back();
      }
    };
  }, [isImagePreviewOpen]);

  const handleShare = async () => {
    if (typeof window === "undefined") {
      return;
    }

    const shareUrl = new URL(
      routes.products.id(product.id, selectedVariant?.id),
      getWebBaseUrlFromEnv(),
    ).toString();

    try {
      const shared = await shareContent({
        title: product.name,
        text: `Check out ${product.name}`,
        url: shareUrl,
      });

      if (shared) {
        return;
      }

      const copied = await copyToClipboard(shareUrl);
      if (copied) {
        toast.success("Link copied to clipboard");
        return;
      }

      toast.error("Unable to share right now");
    } catch {
      toast.error("Unable to share right now");
    }
  };

  const handleOpenImagePreview = (index: number) => {
    setPreviewStartIndex(index);
    setIsImagePreviewOpen(true);
  };

  return (
    <>
      <Carousel className="relative min-h-[50vh]" setApi={setApi}>
        <CarouselContent>
          {selectedVariantImageKeys.map((imageKey, index) => (
            <CarouselItem key={imageKey}>
              <button
                type="button"
                className="block w-full"
                onClick={() => handleOpenImagePreview(index)}
                aria-label={`Preview image ${index + 1}`}
              >
                <CustomImage
                  alt={`${product.name} image ${index + 1}`}
                  className="w-full object-contain object-center"
                  src={imageKey}
                />
              </button>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="left-2" />
        <CarouselNext className="right-2" />
        <div className="absolute top-8 w-full flex px-4 justify-between items-center mb-4">
          <div className="flex justify-baseline items-center gap-3">
            <button
              type="button"
              className="bg-white rounded-full p-2 cursor-pointer shadow-md"
              onClick={() => {
                if (isPreview && onSeePreview) {
                  onSeePreview();
                } else {
                  navigate(-1);
                }
              }}
            >
              <ChevronLeft strokeWidth={1} />
            </button>
          </div>
          <div className="flex justify-end items-center gap-3">
            <button
              type="button"
              className={cn(
                "bg-white rounded-full p-2 cursor-pointer shadow-md",
                isWishlistLoading && "pointer-events-none opacity-60",
              )}
              onClick={() => {
                void handleWishlistToggle();
              }}
            >
              <Heart
                strokeWidth={1}
                className={cn(
                  "transition-colors",
                  isWishlisted && "fill-primary text-primary",
                )}
              />
            </button>
            <button
              type="button"
              className="bg-white rounded-full p-2 cursor-pointer shadow-md"
              onClick={() => {
                void handleShare();
              }}
              aria-label="Share product"
            >
              <Share2 strokeWidth={1} />
            </button>
          </div>
        </div>
        <div className="absolute bottom-5 left-0 right-0">
          <div className="flex justify-between items-center px-4">
            <div className="flex items-center">
              {availableStock <= 0 ? (
                <span className="rounded-full bg-[#F67373] px-4 py-2 font-bold text-md text-white">
                  Out Of Stock
                </span>
              ) : null}
              {availableStock > 0 && availableStock < 10 ? (
                <span className="rounded-full bg-secondary px-4 py-2 font-bold text-md text-white">
                  Only {availableStock} left
                </span>
              ) : null}
            </div>
            {product.isMuminsChoice ||
            product.seller?.shippingCharge ||
            product.orderCount > 0 ? (
              <div
                className={cn(
                  "relative z-10 bg-primary shadow-[0_10px_30px_rgba(0,0,0,0.28)]",
                  product.isMuminsChoice ? "rounded-xl" : "rounded-full",
                )}
              >
                {product.isMuminsChoice ? (
                  <p className="rounded-md p-2 text-xs font-semibold text-white">
                    MUMIN&apos;S CHOICE
                  </p>
                ) : null}
                <p
                  className={cn(
                    "flex rounded-full p-2 items-center justify-center gap-1 bg-[#FF9933] text-[#343434]",
                    product.isMuminsChoice ? "rounded-xl" : "rounded-full",
                  )}
                >
                  {product.seller?.shippingCharge ? (
                    <span className="text-white font-bold">Free Shipping</span>
                  ) : product.orderCount > 0 ? (
                    <span className="text-white font-medium text-md flex items-center gap-1">
                      <span className="text-xl font-bold">
                        {product.orderCount}
                      </span>{" "}
                      units sold
                    </span>
                  ) : null}
                  {/* {reviewSummary.rating}{" "}
                <Star className="h-3 w-3 -[#f7a230] text-[#f7a230]" />
                <span className="text-[#8c8c8c]">
                  {reviewSummary.ratingsCount} ratings
                </span> */}
                </p>
              </div>
            ) : null}
          </div>
          <div className="flex items-center justify-center gap-2 mt-4">
            {Array.from({ length: count }).map((_, index) => (
              <button
                className={cn("h-3 w-3 rounded-full border-2", {
                  "bg-primary": current === index + 1,
                  "w-8": current === index + 1,
                })}
                key={index}
                onClick={() => api?.scrollTo(index)}
              />
            ))}
          </div>
        </div>
      </Carousel>

      <Dialog open={isImagePreviewOpen} onOpenChange={setIsImagePreviewOpen}>
        <DialogContent
          showCloseButton={false}
          className="h-dvh w-screen max-w-none translate-x-[-50%] translate-y-[-50%] rounded-none border-none bg-black p-0 sm:max-w-none"
        >
          <DialogTitle className="sr-only">Product Image Preview</DialogTitle>
          <DialogDescription className="sr-only">
            View product images in fullscreen mode.
          </DialogDescription>

          <div className="relative h-full w-full">
            <button
              type="button"
              className="absolute right-4 top-[calc(1rem+env(safe-area-inset-top))] z-50 rounded-full bg-black/60 p-2.5 text-white backdrop-blur-xs"
              onClick={() => setIsImagePreviewOpen(false)}
              aria-label="Close image preview"
            >
              <X className="size-6" />
            </button>

            <Carousel className="h-full" setApi={setPreviewApi}>
              <CarouselContent className="h-full">
                {selectedVariantImageKeys.map((imageKey, index) => (
                  <CarouselItem
                    key={`${imageKey}-preview`}
                    className="h-full flex items-center justify-center"
                  >
                    <CustomImage
                      alt={`${product.name} image ${index + 1}`}
                      className="h-[88dvh] w-full object-contain object-center"
                      src={imageKey}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>

              {selectedVariantImageKeys.length > 1 ? (
                <>
                  <CarouselPrevious className="left-3 border-white/40 bg-black/40 text-white hover:bg-black/60" />
                  <CarouselNext className="right-3 border-white/40 bg-black/40 text-white hover:bg-black/60" />
                </>
              ) : null}
            </Carousel>

            <div className="absolute bottom-5 left-1/2 z-20 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-sm text-white">
              {previewCurrent || previewStartIndex + 1} /{" "}
              {selectedVariantImageKeys.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductImageCarousel;
